import { useMutation } from '@tanstack/react-query';
import { chatService } from '@/lib/api/services/chat.service';
import { useState } from 'react';

export interface AIHelperMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/**
 * Hook for AI helper chat interface
 * Manages conversation history and generates responses
 */
export function useAIHelper(conversationId?: string | null) {
  const [messages, setMessages] = useState<AIHelperMessage[]>([]);

  const { mutate: askQuestion, isPending } = useMutation({
    mutationFn: async (question: string) => {
      const response = await chatService.getAIHelperResponse(question, conversationId || undefined);
      return { response: response.response, question };
    },
    onSuccess: (data) => {
      const { response, question } = data;
      
      // Add user message immediately (optimistic)
      const userMessage: AIHelperMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: question,
        timestamp: new Date(),
      };

      // Add assistant response
      const assistantMessage: AIHelperMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
    },
  });

  const clearHistory = () => {
    setMessages([]);
  };

  return {
    messages,
    askQuestion,
    isPending,
    clearHistory,
  };
}

